import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import CatalogPage from "./pages/CatalogPage";
import ChangesPage from "./pages/ChangesPage";
import SourcesPage from "./pages/SourcesPage";
import PortfolioPage from "./pages/PortfolioPage";
import AlertsPage from "./pages/AlertsPage";
import AdminPage from "./pages/AdminPage";
import HistoryPage from "./pages/HistoryPage";
import TrendsPage from "./pages/TrendsPage";
import RadarPage from "./pages/RadarPage";
import ReportsPage from "./pages/ReportsPage";
import CalendarPage from "./pages/CalendarPage";
import ProbesPage from "./pages/ProbesPage";
import ChangelogPage from "./pages/ChangelogPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SuperAdminUsuariosPage from "./pages/SuperAdminUsuariosPage";
import SuperAdminStubPage from "./pages/SuperAdminStubPage";
import ActivityPage from "./pages/ActivityPage";
import OrgActivityPage from "./pages/OrgActivityPage";
import SuperAdminGlobalDashboardPage from "./pages/SuperAdminGlobalDashboardPage";
import BrandingPage from "./pages/BrandingPage";
import SeverityPage from "./pages/SeverityPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import NotificationsPage from "./pages/NotificationsPage";
import NotificationPreferencesPage from "./pages/NotificationPreferencesPage";
import SuperAdminAvisosPage from "./pages/SuperAdminAvisosPage";
import SuperAdminContratosPage from "./pages/SuperAdminContratosPage";
import SuperAdminLeadsPage from "./pages/SuperAdminLeadsPage";
import SuperAdminFacturacionPage from "./pages/SuperAdminFacturacionPage";
import SuperAdminDashboardFuentesPage from "./pages/SuperAdminDashboardFuentesPage";
import SuperAdminServicioPage from "./pages/SuperAdminServicioPage";
import OnboardingWizardPage from "./pages/OnboardingWizardPage";
import LandingPage from "./pages/LandingPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CookieConsent from "./components/CookieConsent";
import PriceComparisonPage from "./pages/PriceComparisonPage";
import UpgradePage from "./pages/UpgradePage";
import AppShell from "./components/layout/AppShell";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { useAuthStore } from "./store/authStore";

function HomeRedirect() {
  const role = useAuthStore(s => s.user?.role);
  return <Navigate to={role === "superadmin" ? "/superadmin/dashboard" : "/dashboard"} replace />;
}

/** Raíz pública: landing comercial para visitantes; los usuarios con sesión
 *  van directos a su dashboard. */
function RootRoute() {
  const accessToken = useAuthStore(s => s.accessToken);
  return accessToken ? <HomeRedirect /> : <LandingPage />;
}

function SuperAdminGuard({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const role = useAuthStore(s => s.user?.role);
  const impersonating = useAuthStore(s => s.impersonating);
  return (role === "superadmin" && !impersonating) ? <>{children}</> : <>{fallback}</>;
}

/** Ruta solo para ADMINS de organización (no superadmin, que tienen su propio
 *  registro transversal en /superadmin/actividad). Analyst/readonly → dashboard. */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore(s => s.user?.role);
  return role === "admin" ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

function PlanRoute({ plans, superadminOnly, feature, children }: { plans: string[]; superadminOnly?: boolean; feature?: string; children: React.ReactNode }) {
  const user = useAuthStore(s => s.user);
  if (superadminOnly) return user?.role === "superadmin" ? <>{children}</> : <Navigate to="/dashboard" replace />;
  if (user?.role === "superadmin") return <>{children}</>;
  if (!user?.plan || !plans.includes(user.plan)) {
    return <Navigate to={feature ? `/upgrade?feature=${feature}` : "/upgrade"} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/onboarding" element={<OnboardingWizardPage />} />
            <Route path="/dashboard" element={<SuperAdminGuard fallback={<DashboardPage />}><Navigate to="/superadmin/dashboard" replace /></SuperAdminGuard>} />
            <Route path="/analytics" element={<AnalyticsDashboardPage />} />
            <Route path="/changes" element={<ChangesPage />} />
            <Route path="/sources" element={<SourcesPage />} />
            <Route path="/severity" element={<SeverityPage />} />
            <Route path="/portfolio" element={<Navigate to="/products" replace />} />
            <Route path="/products" element={<PortfolioPage mode="portfolio" />} />
            <Route path="/competitors" element={<PortfolioPage mode="competitors" />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/alerts" element={<PlanRoute plans={["pro","ultra"]} feature="alerts"><AlertsPage /></PlanRoute>} />
            <Route path="/radar" element={<RadarPage />} />
            <Route path="/reports" element={<PlanRoute plans={["pro","ultra"]} feature="reports"><ReportsPage /></PlanRoute>} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/history" element={<HistoryPage />} />
            {/* Solo superadmin: el modelo no tiene histórico suficiente en las agrupaciones
                donde compiten los clientes (ver Sidebar.tsx). No basta con quitarlo del menú:
                un cliente ULTRA llegaría por URL y vería una pantalla vacía. */}
            <Route path="/trends" element={<PlanRoute plans={["ultra"]} superadminOnly feature="trends"><TrendsPage /></PlanRoute>} />
            <Route path="/prices" element={<PlanRoute plans={["ultra"]} feature="prices"><PriceComparisonPage /></PlanRoute>} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/activity" element={<AdminRoute><OrgActivityPage /></AdminRoute>} />
            <Route path="/admin/probes" element={<ProbesPage />} />
            <Route path="/admin/branding" element={<PlanRoute plans={[]} superadminOnly><BrandingPage /></PlanRoute>} />
            <Route path="/changelog" element={<PlanRoute plans={[]} superadminOnly><ChangelogPage /></PlanRoute>} />
            <Route path="/superadmin" element={<SuperAdminPage />} />
            <Route path="/superadmin/dashboard"   element={<PlanRoute plans={[]} superadminOnly><SuperAdminGlobalDashboardPage /></PlanRoute>} />
            <Route path="/superadmin/usuarios"    element={<PlanRoute plans={[]} superadminOnly><SuperAdminUsuariosPage /></PlanRoute>} />
            <Route path="/superadmin/contratos"    element={<PlanRoute plans={[]} superadminOnly><SuperAdminContratosPage /></PlanRoute>} />
            <Route path="/superadmin/leads"        element={<PlanRoute plans={[]} superadminOnly><SuperAdminLeadsPage /></PlanRoute>} />
            <Route path="/superadmin/facturacion"        element={<PlanRoute plans={[]} superadminOnly><SuperAdminFacturacionPage /></PlanRoute>} />
            <Route path="/superadmin/dashboard-fuentes" element={<PlanRoute plans={[]} superadminOnly><SuperAdminDashboardFuentesPage /></PlanRoute>} />
            <Route path="/superadmin/servicio" element={<PlanRoute plans={[]} superadminOnly><SuperAdminServicioPage /></PlanRoute>} />
            <Route path="/superadmin/alertas"     element={<PlanRoute plans={[]} superadminOnly><SuperAdminStubPage title="Alertas globales" icon="⊛" desc="Histórico de alertas enviadas en todas las organizaciones." /></PlanRoute>} />
            <Route path="/superadmin/actividad"   element={<PlanRoute plans={[]} superadminOnly><ActivityPage /></PlanRoute>} />
            <Route path="/superadmin/avisos"      element={<PlanRoute plans={[]} superadminOnly><SuperAdminAvisosPage /></PlanRoute>} />
            <Route path="/profile"                element={<ProfilePage />} />
            <Route path="/notifications"          element={<NotificationsPage />} />
            <Route path="/notifications/preferences" element={<NotificationPreferencesPage />} />
          </Route>
        </Route>
      </Routes>
      <CookieConsent />
    </BrowserRouter>
  );
}
